
import { PlatformData } from "../../types/performance.types";
import { useState } from "react";
import MarginCounter from "./margin-counter";
import { formatPercentage } from "src/pages/reports/components/metric-percent-chart/utils";
import { ArrowDown, ArrowUp } from "lucide-react";
import { setPlatformMargin } from "../../utils/platform-margin";

export interface SpendCardPlatformButtonProps {
  editMode: boolean;
  color: string;
  data: PlatformData;
  onChange: (value: PlatformData) => void;
  disabled?: boolean;
  selectedMetric: string;
}


const SpendCardPlatformButton: React.FC<SpendCardPlatformButtonProps> = ({ editMode = false, color, data, onChange, disabled = false, selectedMetric }) => {
  const [isEnabled, setIsEnabled] = useState(data.enabled);
  const [margin, setMargin] = useState(data.margin);
  const handleMarginChange = (value: number) => {
    setPlatformMargin(data.name, value);
    onChange({
      ...data,
      margin: value,
    });
  };

  const getPercentChange = (): number => {
    const currentValue = data.aggregateData?.[selectedMetric.toLowerCase()];
    const pastValue = data.past?.[selectedMetric.toLowerCase()];

    if (!currentValue || !pastValue || Number(pastValue) === 0) {
      return 0;
    }

    return ((Number(currentValue) - Number(pastValue)) / Number(pastValue)) * 100;
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getMetricValue = (): string => {
    switch (selectedMetric) {
      case 'Clicks':
        return formatNumber(Number(data.aggregateData?.clicks) || 0);
      case 'Spend':
        return formatCurrency((Number(data.aggregateData?.spend) * (1 + data.margin / 100)) || 0);
      case 'Impressions':
        return formatNumber(Number(data.aggregateData?.impressions) || 0);
      default:
        return '0';
    }
  };


  const handleToggle = () => {
    const newEnabled = !isEnabled;
    setIsEnabled(newEnabled);
    onChange({
      ...data,
      enabled: newEnabled,
    });
  };

  return (
    <div
      onClick={handleToggle}
      className={`cursor-pointer p-6 pt-4 pr-1 pb-3 
        ${isEnabled ? `${color} text-white` : 'bg-white text-black border-b border-gray-800'}
        ${!editMode && data.enabled && data.margin > 0 && 'pb-0'}
        `}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <span className={`text-md font-bold ${!isEnabled && 'line-through'}`}>
            {data.displayName}
          </span>
        </div>
        {selectedMetric === 'Spend' && !editMode && data.enabled && (data.margin > 0 || data.margin < 0) && (
          <div>
            <span className="text-xs text-gray-200 mr-2 rounded-full bg-black px-1 py-1">
              {data.margin}
              %
            </span>
          </div>
        ) }
      </div>

      {/* Value Displayed Below */}
      {editMode ? (
        <div className="text-3xl">
          <MarginCounter
            value={data.margin}
            onChange={(value) => handleMarginChange(value)}
            disabled={disabled}
            min={-100}
            max={100}
            step={1}
            className="w-full text-3xl border-none shadow-none bg-transparent"
          />
        </div>
      ) : (
        <div className="text-3xl">
          { getMetricValue() }
          {/* {formatCurrency(Number(data && data.aggregateData && data.aggregateData.spend) * (1 + data.margin / 100))} */}
        </div>
      )}

      {!editMode && data.enabled && (
        <div className="flex justify-between">
          <div className="text-left mb-0 mr-1">
            <div className="text-xs text-white mr-2 flex items-center">
              {getPercentChange() > 0 && (
                <ArrowUp className="w-3 h-3 text-green-800 mr-1 font-medium bg-white rounded-full" />
              )}
              {getPercentChange() < 0 && (
                <ArrowDown className="w-3 h-3 text-red-600 mr-1 font-medium bg-white rounded-full" />
              )}
              {formatPercentage(getPercentChange())}
            </div>
          </div>
          {selectedMetric === 'Spend' && data.margin > 0 && (
            <div className="text-right mb-0 mr-1">
              <div className="text-xs text-white">
                {formatCurrency(Number(data && data.aggregateData && data.aggregateData.spend) * (data.margin / 100))}
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default SpendCardPlatformButton;